import { useEffect, useState } from 'react';
import { TABLET_BREAKPOINT, DESKTOP_BREAKPOINT } from '../constants';
declare global {
	interface Window {
		appKey: string;
		version: string;
		config: any;
	}
}

/**
 * React hook that change webpage title
 *
 * @param title - Is the title to change
 *
 */
export function useDocumentTitle(title: string) {
	useEffect(() => {
		document.title = title;
	}, [title]);
}

/**
 * React hook returns if the application is printing
 *
 * @return value if the application is printing
 *
 */
export function usePrintStatus(): boolean {
	const [printingStatus, setPrintingStatus] = useState<boolean>(false);
	const onBeforePrint = () => {
		setPrintingStatus(true);
	};
	const onAfterPrint = () => {
		setPrintingStatus(false);
	};
	useEffect(() => {
		window.addEventListener('beforeprint', onBeforePrint);
		window.addEventListener('afterprint', onAfterPrint);

		return () => {
			window.removeEventListener('beforeprint', onBeforePrint);
			window.removeEventListener('afterprint', onAfterPrint);
		};
	}, []);
	return printingStatus;
}

/**
 * React hook that returns device information
 *
 * @return Return device information details
 *
 */
export function useDeviceInformation(): DeviceInformation | null {
	const [device, setDevice] = useState<DeviceInformation | null>(null);
	const getDeviceDetails = (): DeviceInformation => {
		const width = document.documentElement.clientWidth;
		const height = document.documentElement.clientHeight;
		const orientation = width > height ? 'landscape' : 'portrait';
		const deviceType =
			width <= TABLET_BREAKPOINT
				? 'mobile'
				: width <= DESKTOP_BREAKPOINT
				? 'tablet'
				: 'desktop';
		const isTouchable = window.matchMedia('(pointer: coarse)').matches;
		return {
			width,
			height,
			orientation,
			type: deviceType,
			isTouchable,
		};
	};
	useEffect(() => {
		setDevice(getDeviceDetails());
		function handleResize() {
			setDevice(getDeviceDetails());
		}
		function handleOrientationChange() {
			handleResize();
		}
		// Bind the event listener
		window.addEventListener('resize', handleResize, true);
		window.addEventListener(
			'orientationchange',
			handleOrientationChange,
			true,
		);
		return () => {
			// Unbind the event listener on clean up
			window.removeEventListener('resize', handleResize);
			window.removeEventListener(
				'orientationchange',
				handleOrientationChange,
			);
		};
	}, []);

	return device;
}

export interface DeviceInformation {
	width: number;
	height: number;
	orientation: 'landscape' | 'portrait';
	type: Device;
	isTouchable: boolean;
}

export type Device = 'mobile' | 'tablet' | 'desktop';
